import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Loader2, 
  ArrowLeft, 
  Users, 
  Crown,
  LogOut,
  UserPlus,
  Trophy,
  Target,
  MessageCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { TeamChat, TeamChatButton } from '../components/social/TeamChat';
import { getImageUrl } from '../utils/imageUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/logo.webp';

export const TeamDetailPage = () => {
  const { challengeId, teamId } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [members, setMembers] = useState([]);
  const [isMember, setIsMember] = useState(false);
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    fetchTeamData();
  }, [challengeId, teamId]);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const res = await api().get(`/challenges/${challengeId}/teams/${teamId}`);
      setTeam(res.data.team);
      setChallenge(res.data.challenge);
      setMembers(res.data.members || []);
      setIsMember(res.data.is_member);
    } catch (err) {
      toast.error('Failed to load team');
      navigate(`/challenges/${challengeId}`);
    }
    setLoading(false);
  };

  const handleJoin = async () => {
    try {
      await api().post(`/challenges/${challengeId}/teams/${teamId}/join`);
      toast.success(`Joined team "${team?.name}"!`);
      fetchTeamData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join team');
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Are you sure you want to leave this team?')) return;
    
    try {
      await api().post(`/challenges/${challengeId}/teams/${teamId}/leave`);
      toast.success('Left team');
      navigate(`/challenges/${challengeId}`);
    } catch (err) {
      toast.error('Failed to leave team');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
      </div>
    );
  }

  if (!team || !challenge) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white">Team not found</p>
      </div>
    );
  }

  const teamSize = members.length;
  const teamTarget = Math.round(challenge.target_value * (challenge.team_target_multiplier || 1) * teamSize);
  const totalProgress = members.reduce((sum, m) => sum + (m.contribution || 0), 0);
  const progressPct = teamTarget > 0 ? Math.min(100, (totalProgress / teamTarget) * 100) : 0;
  const isCaptain = team.captain_id === user?.id;
  const maxTeamSize = challenge.max_team_size || 5;
  const canJoin = !isMember && teamSize < maxTeamSize;

  return (
    <div className="min-h-screen bg-black" data-testid="team-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link 
            to={`/challenges/${challengeId}`} 
            className="flex items-center gap-2 text-white hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back to Challenge</span>
          </Link>
          <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
          <div className="flex gap-2">
            {isMember && (
              <TeamChatButton 
                teamId={teamId} 
                api={api} 
                onOpen={() => setShowChat(true)} 
              />
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Team Info */}
          <div className="lg:col-span-2 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border"
            >
              {/* Team Header */}
              <div className="relative h-32 bg-gradient-to-br from-primary/30 to-primary/5 flex items-center justify-center">
                <Users className="h-16 w-16 text-primary/50" />
                {isMember && (
                  <Badge className="absolute top-4 right-4 bg-primary text-black">
                    Your Team
                  </Badge>
                )}
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="font-heading text-3xl text-white uppercase tracking-wider flex items-center gap-3">
                      <Users className="h-8 w-8 text-primary" />
                      {team.name}
                    </h1>
                    {team.description && (
                      <p className="text-muted-foreground mt-2">{team.description}</p>
                    )}
                    <Link 
                      to={`/challenges/${challengeId}`}
                      className="text-sm text-primary hover:underline mt-2 inline-block"
                    >
                      {challenge.title}
                    </Link>
                  </div>
                  <Badge className="bg-white/10 text-white">
                    {teamSize}/{maxTeamSize} members
                  </Badge>
                </div>

                {/* Team Progress */}
                <div className="bg-white/5 p-6 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-primary" />
                      <span className="text-white font-medium">Team Progress</span>
                    </div>
                    <span className="text-primary font-bold text-lg">
                      {totalProgress} / {teamTarget}
                    </span>
                  </div>
                  <Progress value={progressPct} className="h-4" />
                  <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                    <span>{Math.round(progressPct)}% complete</span>
                    {totalProgress >= teamTarget && (
                      <span className="text-green-400 font-medium flex items-center gap-1">
                        <Trophy className="h-4 w-4" /> Challenge Complete!
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 flex-wrap">
                  {canJoin && (
                    <Button onClick={handleJoin} className="bg-primary text-black">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Join This Team
                    </Button>
                  )}
                  {isMember && !isCaptain && (
                    <Button variant="outline" onClick={handleLeave} className="text-red-400 border-red-400/50">
                      <LogOut className="h-4 w-4 mr-2" />
                      Leave Team
                    </Button>
                  )}
                  {isMember && (
                    <Button 
                      variant="outline" 
                      onClick={() => setShowChat(!showChat)}
                      className={showChat ? 'bg-primary/20 text-primary' : ''}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      {showChat ? 'Hide Chat' : 'Team Chat'}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Team Chat Section (Inline) */}
            {isMember && showChat && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <TeamChat
                  teamId={teamId}
                  api={api}
                  currentUserId={user?.id}
                  teamName={team.name}
                />
              </motion.div>
            )}
          </div>

          {/* Team Members */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-card border border-border"
          >
            <div className="p-4 border-b border-border">
              <h3 className="font-heading text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Team Members
              </h3>
            </div>
            
            <div className="divide-y divide-border">
              {members.map((member, index) => {
                const contributionPct = teamTarget > 0 
                  ? Math.round((member.contribution || 0) / teamTarget * 100)
                  : 0;
                const isCurrentUser = member.user_id === user?.id;
                const memberIsCaptain = member.role === 'captain';
                
                return (
                  <div 
                    key={member.user_id}
                    className={`p-4 flex items-center gap-4 ${isCurrentUser ? 'bg-primary/10' : ''}`}
                  >
                    {/* Rank */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-500 text-black' :
                      index === 1 ? 'bg-gray-400 text-black' :
                      index === 2 ? 'bg-orange-600 text-white' :
                      'bg-white/10 text-white'
                    }`}>
                      {index + 1}
                    </div>
                    
                    {/* Avatar */}
                    <Avatar className="h-10 w-10">
                      <AvatarImage 
                        src={getImageUrl(member.user?.profile_picture)} 
                      />
                      <AvatarFallback className="bg-primary/20 text-primary">
                        {member.user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium truncate">
                          {member.user?.name || 'Unknown'}
                        </span>
                        {memberIsCaptain && (
                          <Crown className="h-4 w-4 text-yellow-400" />
                        )}
                        {isCurrentUser && (
                          <Badge className="bg-primary/20 text-primary text-xs">You</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Progress value={contributionPct} className="h-1.5 flex-1" />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {contributionPct}%
                        </span>
                      </div>
                    </div>
                    
                    {/* Contribution */}
                    <div className="text-right">
                      <div className="text-white font-bold">{member.contribution || 0}</div>
                      <div className="text-xs text-muted-foreground">contribution</div>
                    </div>
                  </div>
                );
              })}
              
              {members.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  No members yet
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default TeamDetailPage;
